import React, { useState } from 'react';
import { useQuery } from 'react-query';
import { salesCyclesApi } from '../services/api';
import TrendsChart from '../components/TrendsChart';
import StageTrendsChart from '../components/StageTrendsChart';
// Temporarily commented out - Stage Trends by Total Criteria component
// import StageTrendsTotalScoreChart from '../components/StageTrendsTotalScoreChart';
import { FilterBar } from '../components/filters';

// Parallel data fetching hook for better performance
const useTrendsPageData = () => {
  // Check if user is authenticated
  const isAuthenticated = !!localStorage.getItem('salesai-api-key');

  // Pre-fetch filter data in parallel - these are cached longer since they change less frequently
  const { data: teamsData } = useQuery(
    'teams',
    salesCyclesApi.getTeams,
    {
      staleTime: 15 * 60 * 1000, // 15 minutes (teams change rarely)
      cacheTime: 30 * 60 * 1000, // 30 minutes
      refetchOnWindowFocus: false,
      enabled: isAuthenticated
    }
  );

  const { data: salesRepsData } = useQuery(
    'salesReps',
    () => salesCyclesApi.getSalesReps(),
    {
      staleTime: 10 * 60 * 1000, // 10 minutes 
      cacheTime: 20 * 60 * 1000, // 20 minutes
      refetchOnWindowFocus: false,
      enabled: isAuthenticated
    }
  );

  return {
    teamsData: teamsData || [],
    salesRepsData: salesRepsData || [],
    isAuthenticated
  };
};


const Trends: React.FC = () => {
  // Use parallel data fetching for better performance
  const { teamsData, salesRepsData } = useTrendsPageData();

  const [activeTab, setActiveTab] = useState<'overview' | 'stage-normalized'>('overview');

  const [selectedCycleTypes, setSelectedCycleTypes] = useState<string[]>(() => {
    // Initialize from localStorage if available, otherwise default to ["NEWBUSINESS"]
    const saved = localStorage.getItem('trends-cycle-type-filter');
    if (!saved) return ['NEWBUSINESS'];

    try {
      // Try to parse as JSON array (new format)
      const parsed = JSON.parse(saved);
      return Array.isArray(parsed) ? parsed : ['NEWBUSINESS'];
    } catch {
      // If parsing fails, it's likely the old single-string format
      // Convert single string to array
      return saved ? [saved] : ['NEWBUSINESS'];
    }
  });

  const [selectedAccountValues, setSelectedAccountValues] = useState<string[]>(() => {
    // Initialize from localStorage if available
    const saved = localStorage.getItem('trends-account-value-filter');
    if (!saved) return [];
    
    try {
      // Try to parse as JSON array
      const parsed = JSON.parse(saved);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      // If parsing fails, clear old format
      localStorage.removeItem('trends-account-value-filter');
      return [];
    }
  });

  const [selectedSalesRep, setSelectedSalesRep] = useState<string | null>(() => {
    // Initialize from localStorage if available
    const saved = localStorage.getItem('trends-sales-rep-filter');
    return saved || null;
  });

  const [selectedTeam, setSelectedTeam] = useState<string | null>(() => {
    // Initialize from localStorage if available
    const saved = localStorage.getItem('trends-team-filter');
    return saved || null;
  });

  const [selectedMeetingDispositions, setSelectedMeetingDispositions] = useState<string[]>(() => {
    // Initialize from localStorage if available
    const saved = localStorage.getItem('trends-meeting-disposition-filter');
    if (!saved) {
      // Default: all dispositions except BAD_FIT and TRASH, including NOT_ANALYZED (Conflict removed)
      return ['TRANSACTIONAL', 'ENTERPRISE', 'NURTURE', 'NOT_ANALYZED'];
    }

    try {
      const parsed = JSON.parse(saved);
      // Filter out CONFLICT from any saved values (legacy cleanup)
      const filtered = Array.isArray(parsed) ? parsed.filter((v: string) => v !== 'CONFLICT') : ['TRANSACTIONAL', 'ENTERPRISE', 'NURTURE', 'NOT_ANALYZED'];
      return filtered.length > 0 ? filtered : ['TRANSACTIONAL', 'ENTERPRISE', 'NURTURE', 'NOT_ANALYZED'];
    } catch {
      return ['TRANSACTIONAL', 'ENTERPRISE', 'NURTURE', 'NOT_ANALYZED'];
    }
  });

  const handleCycleTypesChange = (cycleTypes: string[]) => {
    setSelectedCycleTypes(cycleTypes);
    // Save to localStorage
    if (cycleTypes.length > 0) {
      localStorage.setItem('trends-cycle-type-filter', JSON.stringify(cycleTypes));
    } else {
      localStorage.removeItem('trends-cycle-type-filter');
    }
  };

  const handleAccountValuesChange = (accountValues: string[]) => {
    setSelectedAccountValues(accountValues);
    // Save to localStorage
    if (accountValues.length > 0) {
      localStorage.setItem('trends-account-value-filter', JSON.stringify(accountValues));
    } else {
      localStorage.removeItem('trends-account-value-filter');
    }
  };

  const handleSalesRepChange = (salesRep: string | null) => {
    setSelectedSalesRep(salesRep);
    // Save to localStorage
    if (salesRep) {
      localStorage.setItem('trends-sales-rep-filter', salesRep);
    } else {
      localStorage.removeItem('trends-sales-rep-filter');
    }
  };

  const handleTeamChange = (team: string | null) => {
    setSelectedTeam(team);
    // Clear the selected sales rep when team changes
    setSelectedSalesRep(null);

    // Save to localStorage
    if (team) {
      localStorage.setItem('trends-team-filter', team);
    } else {
      localStorage.removeItem('trends-team-filter');
    }

    // Clear sales rep from localStorage
    localStorage.removeItem('trends-sales-rep-filter');
  };

  const handleMeetingDispositionsChange = (dispositions: string[]) => {
    setSelectedMeetingDispositions(dispositions);
    // Save to localStorage
    if (dispositions.length > 0) {
      localStorage.setItem('trends-meeting-disposition-filter', JSON.stringify(dispositions));
    } else {
      localStorage.removeItem('trends-meeting-disposition-filter');
    }
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col space-y-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 font-plus-jakarta">Performance Trends</h1>
          <p className="text-gray-600 dark:text-gray-400 text-sm font-inter mt-1">
            Track deal scoring performance over time with interactive charts
          </p>
        </div>

        {/* Filters */}
        <FilterBar
          selectedCycleTypes={selectedCycleTypes}
          selectedAccountValues={selectedAccountValues}
          selectedMeetingDispositions={selectedMeetingDispositions}
          selectedTeam={selectedTeam}
          selectedSalesRep={selectedSalesRep}
          onCycleTypesChange={handleCycleTypesChange}
          onAccountValuesChange={handleAccountValuesChange}
          onMeetingDispositionsChange={handleMeetingDispositionsChange}
          onTeamChange={handleTeamChange}
          onSalesRepChange={handleSalesRepChange}
          collapsible={true}
          defaultCollapsed={true}
        />
      </div>

      {/* Tab Navigation */}
      <div className="border-b border-gray-200 dark:border-gray-700">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('overview')}
            className={`py-2 px-1 border-b-2 font-medium text-sm font-inter ${
              activeTab === 'overview'
                ? 'border-purple-500 text-purple-600 dark:text-purple-400'
                : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:border-gray-300 dark:hover:border-gray-600'
            }`}
          >
            Overview Trends
          </button>
                    <button
                        onClick={() => setActiveTab('stage-normalized')}
                        className={`py-2 px-1 border-b-2 font-medium text-sm font-inter ${
                            activeTab === 'stage-normalized'
                                ? 'border-purple-500 text-purple-600 dark:text-purple-400'
                                : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:border-gray-300 dark:hover:border-gray-600'
                        }`}
                    >
                        Stage Trends
                    </button>
                    {/* Temporarily commented out - Stage Trends by Total Criteria tab */}
                    {/* <button
                        onClick={() => setActiveTab('stage-total-score')}
                        className={`py-2 px-1 border-b-2 font-medium text-sm font-inter ${
                            activeTab === 'stage-total-score'
                                ? 'border-purple-500 text-purple-600'
                                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                        }`}
                    >
                        Stage Trends (by Total Criteria)
                    </button> */}
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && (
        <>
          {/* Deal Scoring Trends Chart */}
          <TrendsChart
            selectedCycleTypes={selectedCycleTypes}
            selectedAccountValues={selectedAccountValues}
            selectedSalesRep={selectedSalesRep}
            selectedTeam={selectedTeam}
          />
        </>
      )}

      {activeTab === 'stage-normalized' && (
        <>
          {/* Deal Stage Trends Chart */}
          <StageTrendsChart
            selectedCycleTypes={selectedCycleTypes}
            selectedAccountValues={selectedAccountValues}
            selectedSalesRep={selectedSalesRep}
            selectedTeam={selectedTeam}
          />
        </>
      )}

      {/* Temporarily commented out - Stage Trends by Total Criteria content */}
      {/* {activeTab === 'stage-total-score' && (
        <>
          Stage Trends with Total Score Method
          <StageTrendsTotalScoreChart
            selectedCycleTypes={selectedCycleTypes}
            selectedAccountValues={selectedAccountValues}
            selectedSalesRep={selectedSalesRep}
            selectedTeam={selectedTeam}
          />
        </>
      )} */}
    </div>
  );
};

export default Trends;